import { useCallback, useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { useLatestCallbackRef } from '../../../Hooks/useLatestCallbackRef';
import { createStore, ReadonlyStore } from '../../../utils/store';
import { identity } from '../../../utils/fn';

export interface CommonUseBubblesProps {
    enabled: boolean;
    onNewBubble: () => void;
}

interface UseBubblesProps<B extends { id: number }, TB extends { id: number } = B> extends CommonUseBubblesProps {
    updateEventName: string;
    getBubbles: () => Promise<B[]>;

    client?: Socket;
    transform?: (bubble: B) => TB;
}

const removedBubbleIds: Set<number> = new Set();

export function useBubbles<B extends { id: number }, Update extends { oldIds: Array<number>; current: B }, TB extends { id: number } = B>({
    enabled,
    getBubbles,
    updateEventName,

    client,
    transform,

    onNewBubble,
}: UseBubblesProps<B, TB>) {
    const storeRef = useRef(
        createStore<{
            bubbles: TB[];
            loading: boolean | null;
        }>({
            bubbles: [],
            loading: null,
        }),
    );

    const newBubbleHandlerRef = useLatestCallbackRef(onNewBubble);
    const transformRef = useLatestCallbackRef<(bubble: B) => TB>(transform ?? (identity as (bubble: B) => TB));

    useEffect(() => {
        if (!enabled) return;

        const fetchData = async () => {
            try {
                storeRef.current.set({ bubbles: [], loading: true });

                const latest = await getBubbles();

                newBubbleHandlerRef.current();

                storeRef.current.set({ bubbles: latest.map(transformRef.current), loading: false });
            } catch (error) {
                console.error('Failed to fetch bubbles', error);
            }
        };

        fetchData();
    }, [enabled, getBubbles, newBubbleHandlerRef, transformRef]);

    useEffect(() => {
        if (!client) return;

        if (enabled) {
            client.on(updateEventName, async (event: Update) => {
                const stillExistingBubbles = storeRef.current.get().bubbles.filter((bubble) => !event.oldIds.includes(bubble.id));

                if (event.oldIds.some((id) => removedBubbleIds.has(id))) {
                    removedBubbleIds.add(event.current.id);
                    event.oldIds.forEach((id) => removedBubbleIds.delete(id));
                } else {
                    newBubbleHandlerRef.current();
                }

                storeRef.current.set({
                    bubbles: removedBubbleIds.has(event.current.id)
                        ? stillExistingBubbles
                        : [transformRef.current(event.current), ...stillExistingBubbles].slice(0, 20),
                    loading: false,
                });
            });
        }

        return () => {
            client.off(updateEventName);
        };
    }, [enabled, client, updateEventName, newBubbleHandlerRef, transformRef]);

    const removeBubble = useCallback((id: number) => {
        storeRef.current.set({ bubbles: storeRef.current.get().bubbles.filter((bubble) => bubble.id !== id), loading: false });
        removedBubbleIds.add(id);
    }, []);

    return [
        storeRef.current as ReadonlyStore<{
            bubbles: TB[];
            loading: boolean | null;
        }>,
        removeBubble,
    ] as const;
}
